import axiosApi from './axiosApi';
import {tokenStorage} from '../app/tokenStorage';
import {encrypt} from './securityApi';
import {localDecrypt} from '../utils/localDecrypt';
import {POST_TASK, GET_TASKS_LIST_SEARCH, GET_ARCHIVED_TASKS_LIST_SEARCH, POST_TASK_OBSERVATION, GET_TASK_HISTORY, UPDATE_TASK_ACCOMPLISHED, UPDATE_TASK_MOBILE, UPLOAD_TASK_FILES, POST_EMAIL_FROM_TASK, GET_TASK_TAGS, POST_ADD_TAG, DELETE_TAGS, GET_RECEIVED_TASKS, GET_SENT_TASKS, GET_ASSIGNEE_LIST, GET_OWNERS, GET_NOTIFIERS, POST_START_ACTIVITY, PUT_UPDATE_SUMMARY, POST_CREATE_POMODORO_TIMER, PUT_END_POMODORO_TIMER, POST_UPDATE_TIME_SPENT, GET_SUMMARY, GET_SUMMARY_HISTORY, ARCHIVE_TASKS, UNARCHIVE_TASKS, DELETE_TASKS, POST_APPROVE_DAY_OFF, POST_DECLINE_DAY_OFF} from './routes/tasks';
import {DOWNLOAD_SERVER, API_BASE_URL} from '../app/config';

/**
 * Clean task update payload by removing invalid fields that backend doesn't accept
 * @param {Object} data - The payload data to clean
 * @returns {Object} Cleaned payload without invalid fields
 */
const cleanTaskUpdatePayload = (data) => {
    // Create a copy to avoid mutating the original
    const cleanData = { ...data };
    
    // Remove fields that are not supported by backend or cause errors
    delete cleanData.project; // Backend doesn't accept 'project' field
    delete cleanData.tag; // Backend doesn't accept 'tag' field (use 'type' instead if needed)
    delete cleanData.duration; // Already ignored in backend, but remove to be safe
    delete cleanData.addToHistory; // Already ignored in backend
    delete cleanData.notification_data; // Processed separately
    delete cleanData.userID; // Already in URL
    // Note: assigneeID, owner, responsible fields are now allowed in update payload
    // They will be sent to UPDATE_TASK_ACCOMPLISHED endpoint
    
    return cleanData;
};

/**
 * Fast local decryption - NO remote calls, instant response
 * @param {string} data - Encrypted string to decrypt
 * @returns {Object|null} Decrypted object or null
 */
const fastDecrypt = (data) => {
    if (!data) return null;
    
    // If already object, return it
    if (typeof data === 'object') return data;
    
    // If string, try to decrypt locally
    if (typeof data === 'string') {
        // Try JSON parse first (maybe not encrypted)
        try {
            return JSON.parse(data);
        } catch {}
        
        // Try local decryption (instant, no network)
        try {
            const decrypted = localDecrypt(data);
            if (decrypted) {
                try {
                    return JSON.parse(decrypted);
                } catch {
                    return decrypted;
                }
            }
        } catch {}
        
        // Try Base64 decode
        try {
            const decoded = atob(data);
            return JSON.parse(decoded);
        } catch {}
    }
    
    return null;
};

/**
 * Get media URL for task files
 * @param {number} missionId - The mission/task ID
 * @param {string} mediaName - The media file name
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {string} The complete media URL with authentication
 */
export const getMediaUrl = (missionId, mediaName, serviceProvider = 59) => {
    if (!missionId || !mediaName) {
        throw new Error('Mission ID and media name are required');
    }

    const baseUrl = (DOWNLOAD_SERVER || '').replace(/\/$/, '');
    if (!baseUrl) {
        throw new Error('DOWNLOAD_SERVER is not configured');
    }

    const url = `${baseUrl}/easeetask/files/preview/${serviceProvider}/${missionId}/${mediaName}`;
    return url;
};

/**
 * Get organisation preview URL for organisation files (logos, etc.)
 * @param {number} organisationID - The organisation ID
 * @param {string} fileName - The file name
 * @returns {string} The complete organisation preview URL with authentication
 */
export const getOrganisationPreview = (organisationID, fileName) => {
    if (!organisationID || !fileName) {
        throw new Error('Organisation ID and file name are required');
    }

    const baseUrl = (DOWNLOAD_SERVER || '').replace(/\/$/, '');
    if (!baseUrl) {
        throw new Error('DOWNLOAD_SERVER is not configured');
    }

    const url = `${baseUrl}/easeetask/organisation-files/preview/${organisationID}/${fileName}`;
    return url;
};

/**
 * Helper function to get authenticated image URL (for use with fetch/blob)
 * This can be used when you need to load images with authentication headers
 * @param {string} url - The media URL
 * @returns {Promise<string>} A blob URL that can be used in img src
 */
export const getAuthenticatedImageUrl = async (url) => {
    const token = tokenStorage.getToken();
    if (!token) {
        throw new Error('Authorization token is missing');
    }

    try {
        const response = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        throw error;
    }
};

/**
 * Update task total time spent
 * @param {number} taskID - The task ID
 * @param {number} timeInMinutes - The total time spent in minutes
 * @returns {Promise<Object>} The response data
 */
export const updateTaskTimeSpent = async (taskID, timeInMinutes) => {
    try {
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }

        const endpoint = POST_UPDATE_TIME_SPENT(taskID);
        const payload = { total_time_spent: timeInMinutes };
        
        // Encrypt the payload
        const encryptedData = await encrypt(payload);
        
        // Handle different encryption response structures
        let payloadToSend = encryptedData;
        if (encryptedData && typeof encryptedData === 'object') {
            if (encryptedData.data) {
                payloadToSend = encryptedData.data;
            }
        }
        
        if (Array.isArray(payloadToSend)) {
            if (payloadToSend.length > 0) {
                payloadToSend = payloadToSend[0];
            }
        }

        const response = await axiosApi.post(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': userId.toString()
            }
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new task
 * @param {Object} taskData - The task data to create
 * @returns {Promise<Object>} The created task data
 */
export const postTask = async (taskData) => {
    if (!taskData) {
        throw new Error('Task data is required');
    }

    try {
        // Encrypt the task data - exact ca registerApi
        let encryptedData = await encrypt(taskData);

        // Backend expects a string, not an array
        // If encryptedData.data is an array, extract the first element (the encrypted string)
        let payloadToSend = encryptedData.data;
        
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
}
        } else {
        }

        // Backend expects JUST the encrypted string as RAW TEXT
        // NOT as JSON, NOT as array, NOT wrapped in anything
        // Use transformRequest to prevent axios from JSON.stringify-ing it
        let response = await axiosApi.post(POST_TASK, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => {
                // Return data as-is, don't let axios convert it to JSON
                return data;
            }]
        });
        // Check if response is valid
        if (!response || !response.data) {
            throw new Error('Invalid response from server');
        }
        
        // Exact ca registerApi - decrypt response.data
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If decrypt fails with 400, it returns {data: originalString}
            if (decryptError?.response?.status === 400) {
                const localDecrypted = localDecryptFallback(response.data);
                if (localDecrypted) {
                    return localDecrypted;
                }
                // If local decryption fails but response status is 200, task was created successfully
                if (response?.status === 200) {
                    return {
                        success: true,
                        message: 'Task-ul a fost creat cu succes, dar nu s-a putut decripta răspunsul.',
                        status: 200,
                        encryptedResponse: response.data // Include for debugging
                    };
                }
            }
            throw decryptError;
        }
        
        // Check if decrypt returned wrapped string (400 error case)
        if (decryptedResponse?.data && typeof decryptedResponse.data === 'string') {
            try {
                const parsed = JSON.parse(decryptedResponse.data);
                return parsed;
            } catch (e) {
                const localDecrypted = localDecryptFallback(decryptedResponse.data);
                if (localDecrypted) {
                    return localDecrypted;
                }
                // If all decryption methods fail but response status is 200, task was created successfully
                // Return a success object instead of throwing an error
                if (response?.status === 200) {
                    return {
                        success: true,
                        message: 'Task-ul a fost creat cu succes, dar nu s-a putut decripta răspunsul complet.',
                        status: 200,
                        encryptedResponse: decryptedResponse.data // Include for debugging
                    };
                }
                // If status is not 200, throw an error
                
                const error = new Error('Nu s-a putut decripta răspunsul de la server. Endpoint-ul de decriptare nu funcționează corect.');
                error.encryptedData = decryptedResponse.data; // Include encrypted data for debugging
                throw error;
            }
        }
        
        // Exact ca registerApi - return decryptedResponse.data
        const result = decryptedResponse?.data || decryptedResponse;
        return result;
    } catch (error) {
        throw error;
    }
};

/**
 * Helper function to fix Base64 padding
 * @param {string} base64String - Base64 string that might need padding
 * @returns {string} Base64 string with correct padding
 */
const fixBase64Padding = (base64String) => {
    // Remove any whitespace
    let cleaned = base64String.trim().replace(/\s/g, '');
    
    // Base64 strings should have length that is a multiple of 4
    const remainder = cleaned.length % 4;
    if (remainder !== 0) {
        // Add padding
        cleaned += '='.repeat(4 - remainder);
    }
    
    return cleaned;
};

/**
 * Local decryption fallback - tries to decode Base64 and parse as JSON
 * @param {string} encryptedString - The encrypted string to decrypt
 * @returns {Object|null} Decrypted object or null if failed
 */
const localDecryptFallback = (encryptedString) => {
    // Try multiple Base64 decoding approaches
    const attempts = [
        { name: 'Direct Base64 decode', string: encryptedString },
        { name: 'Base64 with padding fix', string: fixBase64Padding(encryptedString) },
        { name: 'Base64 with URL-safe chars replaced', string: fixBase64Padding(encryptedString.replace(/-/g, '+').replace(/_/g, '/')) }
    ];
    
    for (const attempt of attempts) {
        try {
            const base64Decoded = atob(attempt.string);

            // Try to parse as JSON
            try {
                const parsed = JSON.parse(base64Decoded);
                
                return parsed;
            } catch (jsonError) {
                // Continue to next attempt
            }
        } catch (base64Error) {
            // Continue to next attempt
        }
    }
    return null;
};

/**
 * Add an observation/update to a task
 * @param {number} taskID - The task ID
 * @param {string} value - The observation/update message
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} service_provider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The response data
 */
export const addTaskObservation = async (taskID, value, userID = null, service_provider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }
    if (!value || !value.trim()) {
        throw new Error('Observation value is required');
    }

    try {
        const userId = userID || tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }

        const observationData = {
            value: value.trim(),
            userID: Number(userId),
            service_provider: Number(service_provider)
        };
        // Encrypt the observation data
        const encryptedData = await encrypt(observationData);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = POST_TASK_OBSERVATION(taskID);
        // Send encrypted data as raw string
        const response = await axiosApi.post(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        // Decrypt response
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Observation added successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }

        // Parse decrypted response
        if (decryptedResponse?.data && typeof decryptedResponse.data === 'string') {
            try {
                const parsed = JSON.parse(decryptedResponse.data);
                return parsed;
            } catch (e) {
                if (response?.status === 200) {
                    return {
                        success: true,
                        message: 'Observation added successfully.',
                        status: 200
                    };
                }
                throw new Error('Failed to parse response from server.');
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Get task history/updates
 * @param {number} taskID - The task ID
 * @param {string} desc - Description parameter (default: 'desc')
 * @returns {Promise<Array>} Array of history entries (all field changes: comments, dates, status, etc.)
 */
export const getTaskHistory = async (taskID, desc = 'desc') => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }

    try {
        const endpoint = GET_TASK_HISTORY(taskID, desc);
        const response = await axiosApi.get(endpoint, {
            timeout: 60000,
        });
        // Decrypt response if needed
        let decryptedData = response.data;
        
        // Check if response is encrypted (string)
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedData = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedData = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }

        // Extract data array from response
        const historyData = decryptedData?.data || decryptedData || [];
        
        // Return all history entries, not just comments
        const allEntries = Array.isArray(historyData) ? historyData : [];
        return allEntries.map(entry => ({
            field_name: entry.field_name || entry.fieldName || '',
            old_value: entry.old_value || entry.oldValue || '',
            new_value: entry.new_value || entry.newValue || entry.value || '',
            message: entry.message || entry.new_value || entry.newValue || entry.value || '', // Use message field from API
            user: entry.userName || entry.user_name || entry.creator || entry.assignee || 'User',
            timestamp: entry.date || entry.timestamp || new Date().toISOString(),
            project_id: entry.project_id || entry.projectId || null,
            avatarURL: entry.avatarURL || entry.avatar || entry.creator_avatarURL || entry.assignee_avatarURL || entry.user_avatar || null
        }));
    } catch (error) {
        // Return empty array on error instead of throwing
        return [];
    }
};

/**
 * Fetch tasks with optional filters - OPTIMIZED VERSION
 * Uses local decryption directly for faster response
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} The tasks data
 */
export const getTasks = async ({
    limit,
    offset,
    q,
    advanced,
    searchUserID,
    search,
    pocket,
    order,
    orderBy
} = {}) => {
    try {
        const params = {};
        params.limit = (limit !== undefined && limit !== null) ? limit : 10;
        if (offset !== undefined && offset !== null) params.offset = offset;
        if (q) params.q = q;
        if (advanced) params.advanced = advanced;
        if (searchUserID !== undefined && searchUserID !== null) params.searchUserID = searchUserID;
        if (search) params.search = search;
        if (pocket) params.pocket = pocket;
        if (order) params.order = order;
        if (orderBy) params.orderBy = orderBy;

        const userId = tokenStorage.getUserId();
        const headers = { 'Accept': 'application/json' };
        if (userId) headers['userID'] = userId.toString();
        
        const response = await axiosApi.get(GET_TASKS_LIST_SEARCH, {
            headers,
            params: { ...params, encrypted: false },
        });
        try {
            const fullUrl = `${GET_TASKS_LIST_SEARCH}?${new URLSearchParams({ ...params, encrypted: false }).toString()}`;
            console.log('🌐 [getTasks] Request', fullUrl);
        } catch {}

        if (!response?.data) {
            throw new Error('Empty API response');
        }

        // If already an object, return directly
        if (typeof response.data === 'object') {
            console.log('📋 [getTasks] API Response structure:', JSON.stringify(response.data, null, 2));
            return response.data;
        }

        // If string, decrypt locally (FAST - no remote calls)
        if (typeof response.data === 'string') {
            // Try parsing as JSON first
            try {
                return JSON.parse(response.data);
            } catch {}
            
            // Use LOCAL decryption only - instant, no network calls
            try {
                const decrypted = localDecrypt(response.data);
                if (decrypted) {
                    try {
                        return JSON.parse(decrypted);
                    } catch {}
                }
            } catch {}
            
            // Fallback to Base64 decode
            try {
                const decoded = atob(response.data);
                return JSON.parse(decoded);
            } catch {}
            
            // Return empty if decryption fails
            return { data: { records: [], count: 0, total: 0 } };
        }

        return { data: { records: [], count: 0, total: 0 } };
    } catch (error) {
        throw error;
    }
};

/**
 * Get archived tasks
 * @param {Object} params - Query parameters
 * @param {string} params.pocket - Required: 'planned', 'doing', 'to do', or 'done'
 * @param {number} params.limit - Number of records to return (default: 10)
 * @param {number} params.offset - Offset for pagination
 * @param {string} params.q - Query string
 * @param {number} params.searchUserID - User ID for search
 * @param {string} params.search - Search text
 * @param {string} params.orderBy - Order by field
 * @param {string} params.order - Order direction (asc/desc)
 * @param {string} params.fields - Fields to return
 * @returns {Promise<Object>} Archived tasks response
 */
export const getArchivedTasks = async ({
    pocket,
    limit,
    offset,
    q,
    searchUserID,
    search,
    orderBy,
    order,
    fields
} = {}) => {
    if (!pocket) {
        throw new Error('pocket parameter is required for archived tasks');
    }

    try {
        const params = {};
        params.pocket = pocket; // Required
        params.limit = (limit !== undefined && limit !== null) ? limit : 10;
        if (offset !== undefined && offset !== null) params.offset = offset;
        if (q) params.q = q;
        if (searchUserID !== undefined && searchUserID !== null) params.searchUserID = searchUserID;
        if (search) params.search = search;
        if (orderBy) params.orderBy = orderBy;
        if (order) params.order = order;
        if (fields) params.fields = fields;

        const userId = tokenStorage.getUserId();
        const headers = { 'Accept': 'application/json' };
        if (userId) headers['userID'] = userId.toString();
        
        const response = await axiosApi.get(GET_ARCHIVED_TASKS_LIST_SEARCH, {
            headers,
            params: { ...params, encrypted: false },
        });

        if (!response?.data) {
            throw new Error('Empty API response');
        }

        // If already an object, return directly
        if (typeof response.data === 'object') {
            return response.data;
        }

        // If string, decrypt locally (FAST - no remote calls)
        if (typeof response.data === 'string') {
            // Try parsing as JSON first
            try {
                return JSON.parse(response.data);
            } catch {}
            
            // Use LOCAL decryption only - instant, no network calls
            try {
                const decrypted = localDecrypt(response.data);
                if (decrypted) {
                    try {
                        return JSON.parse(decrypted);
                    } catch {}
                }
            } catch {}
            
            // Fallback to Base64 decode
            try {
                const decoded = atob(response.data);
                return JSON.parse(decoded);
            } catch {}
            
            // Return empty if decryption fails
            return { data: { records: [], count: 0, total: 0 } };
        }

        return { data: { records: [], count: 0, total: 0 } };
    } catch (error) {
        throw error;
    }
};

/**
 * Upload files for a task
 * @param {File|File[]} files - Single file or array of files to upload
 * @param {number} projectID - The project/task ID
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The upload response
 */
export const uploadTaskFiles = async (files, projectID, userID = null, serviceProvider = 59) => {
    if (!files || (Array.isArray(files) && files.length === 0)) {
        throw new Error('At least one file is required');
    }
    if (!projectID) {
        throw new Error('Project ID is required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for uploading files');
    }

    try {
        // Create FormData – backend expects array key "file[]"
        const formData = new FormData();
        
        // Handle single file or array of files
        const filesArray = Array.isArray(files) ? files : [files];
        filesArray.forEach((file, index) => {
            if (file instanceof File) {
                formData.append('file[]', file);
            } else {
            }
        });

        const endpoint = UPLOAD_TASK_FILES(serviceProvider, projectID, actualUserID);
        // Upload files using multipart/form-data
        const response = await axiosApi.post(endpoint, formData, {
            headers: {
                // do not set Content-Type manually; let the browser set boundary
                userID: String(actualUserID),
            },
            timeout: 300000, // 5 minutes timeout for large files
        });
        // Try to decrypt response if it's encrypted
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Archive tasks
 * @param {number} userID - ID of the user performing the archive
 * @param {number[]} projectIDs - Array of task IDs to archive
 * @returns {Promise<Object>} Archive response
 */
export const archiveTasks = async (userID, projectIDs) => {
    if (!userID) {
        throw new Error('User ID is required for archiving tasks');
    }
    if (!projectIDs || !Array.isArray(projectIDs) || projectIDs.length === 0) {
        throw new Error('At least one project ID is required to archive');
    }

    try {
        const endpoint = ARCHIVE_TASKS(userID);
        // Encrypt payload similar to other secure endpoints
        const payload = { projectIDs };
        const encrypted = await encrypt(payload);
        let payloadToSend = encrypted?.data;
        if (Array.isArray(payloadToSend) && payloadToSend.length > 0) {
            payloadToSend = payloadToSend[0];
        }

        const response = await axiosApi.put(
            endpoint,
            payloadToSend,
            {
                headers: {
                    userID: String(userID),
                    'Content-Type': 'text/plain'
                },
                transformRequest: [(data) => data] // send raw encrypted string
            }
        );

        let data = response.data;
        if (typeof data === 'string') {
            try {
                const decrypted = fastDecrypt(data);
                data = decrypted?.data || decrypted;
            } catch (err) {
            }
        }

        return data?.data || data;
    } catch (error) {
        throw error;
    }
};

export const unarchiveTasks = async (userID, projectIDs) => {
    if (!userID) {
        throw new Error('User ID is required for unarchiving tasks');
    }
    if (!projectIDs || !Array.isArray(projectIDs) || projectIDs.length === 0) {
        throw new Error('At least one project ID is required to unarchive');
    }

    try {
        const endpoint = UNARCHIVE_TASKS(userID);
        const payload = { projectIDs };
        const encrypted = await encrypt(payload);
        let payloadToSend = encrypted?.data;
        if (Array.isArray(payloadToSend) && payloadToSend.length > 0) {
            payloadToSend = payloadToSend[0];
        }

        const response = await axiosApi.put(
            endpoint,
            payloadToSend,
            {
                headers: {
                    userID: String(userID),
                    'Content-Type': 'text/plain'
                },
                transformRequest: [(data) => data]
            }
        );

        let data = response.data;
        if (typeof data === 'string') {
            try {
                const decrypted = fastDecrypt(data);
                data = decrypted?.data || decrypted;
            } catch (err) {
            }
        }

        return data?.data || data;
    } catch (error) {
        throw error;
    }
};

export const deleteTasks = async (userID, projectIDs) => {
    if (!userID) {
        throw new Error('User ID is required for deleting tasks');
    }
    if (!projectIDs || !Array.isArray(projectIDs) || projectIDs.length === 0) {
        throw new Error('At least one project ID is required to delete');
    }

    try {
        const endpoint = DELETE_TASKS(userID);
        const payload = { projectIDs };
        const encrypted = await encrypt(payload);
        let payloadToSend = encrypted?.data;
        if (Array.isArray(payloadToSend) && payloadToSend.length > 0) {
            payloadToSend = payloadToSend[0];
        }

        const response = await axiosApi.delete(endpoint, {
            headers: {
                userID: String(userID),
                'Content-Type': 'text/plain'
            },
            data: payloadToSend,
            transformRequest: [(data) => data]
        });

        let data = response.data;
        if (typeof data === 'string') {
            try {
                const decrypted = fastDecrypt(data);
                data = decrypted?.data || decrypted;
            } catch (err) {
            }
        }

        return data?.data || data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task accomplished value
 * @param {number} taskID - The task ID to update
 * @param {number} accomplished - The accomplished value (0-100)
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskAccomplished = async (taskID, accomplished, userID = null, serviceProvider = 59) => {
    if (!taskID || accomplished === undefined || accomplished === null) {
        throw new Error('Task ID and accomplished value are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Payload should contain only accomplished value as per requirements: {"accomplished": 25}
        const payload = {
            accomplished: Number(accomplished)
        };
        // Encrypt the payload (backend expects encrypted data)
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);

        // Send encrypted data as raw string (backend expects encrypted data)
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task assignee/owner
 * @param {number} taskID
 * @param {number} assigneeID
 * @param {number} userID
 * @param {number} serviceProvider
 */
export const updateTaskAssignee = async (taskID, assigneeID, userID = null, serviceProvider = 59) => {
    if (!taskID || !assigneeID) {
        throw new Error('Task ID and assignee ID are required');
    }
    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) throw new Error('User ID is required');

    // Use 'responsible' field for update general endpoint (same as task creation)
    // Add addToHistory: true and assignee field for Done tasks duplication
    const payload = { 
        responsible: Number(assigneeID),
        addToHistory: true,
        assignee: Number(assigneeID)
    };
    let encryptedData = await encrypt(payload);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(payloadToSend) && payloadToSend.length > 0) {
        payloadToSend = payloadToSend[0];
    }

    try {
        // Use UPDATE_TASK_ACCOMPLISHED endpoint instead of UPDATE_TASK_ASSIGNEE
        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain'
            },
            transformRequest: [(data) => data]
        });
        let data = response.data;
        if (typeof data === 'string') {
            try {
                const decrypted = fastDecrypt(data);
                data = decrypted?.data || decrypted;
            } catch (err) {
            }
        }
        return data?.data || data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task assignees/owners (multiple)
 * @param {number} taskID - The task ID to update
 * @param {Array<number>} assigneeIds - Array of assignee user IDs
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskAssignees = async (taskID, assigneeIds, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Convert assigneeIds to array of numbers
        const assignees = Array.isArray(assigneeIds) 
            ? assigneeIds.map(id => Number(id))
            : (assigneeIds ? [Number(assigneeIds)] : []);
        
        // Prepare payload - use responsible field for multiple assignees
        const payload = {
            responsible: assignees
        };

        // Encrypt the payload (server expects encrypted data)
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_MOBILE(serviceProvider, taskID, actualUserID);
        
        // Send encrypted data as raw string (backend expects encrypted data)
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        
        // Decrypt response
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task title
 * @param {number} taskID - The task ID to update
 * @param {string} title - The new title
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskTitle = async (taskID, title, userID = null, serviceProvider = 59) => {
    if (!taskID || !title) {
        throw new Error('Task ID and title are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            title: String(title)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task description
 * @param {number} taskID - The task ID to update
 * @param {string} description - The new description
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskDescription = async (taskID, description, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            description: description ? String(description) : ''
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task start_date
 * @param {number} taskID - The task ID to update
 * @param {string} startDate - The start date in format "YYYY-MM-DD"
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskStartDate = async (taskID, startDate, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }
    // Allow empty string or "0" for deletion
    if (startDate === undefined || startDate === null) {
        throw new Error('Start date is required (use empty string or "0" to delete)');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Use "0" for deletion, otherwise use the provided date
        const payload = {
            start_date: (startDate === '' || startDate === '0') ? '0' : String(startDate)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task priority
 * @param {number} taskID - The task ID to update
 * @param {number} priority - The priority value (1=Standard, 2=Important, 3=Urgent, 4=Important and Urgent)
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskPriority = async (taskID, priority, userID = null, serviceProvider = 59) => {
    if (!taskID || priority === undefined || priority === null) {
        throw new Error('Task ID and priority are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            priority: Number(priority)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task types (projects and tags)
 * @param {number} taskID - The task ID to update
 * @param {Array<number>} types - Array of type/project IDs
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskTypes = async (taskID, types, userID = null, serviceProvider = 59) => {
    if (!taskID || !types) {
        throw new Error('Task ID and types are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Convert types array to the format expected by API
        const typesArray = Array.isArray(types) ? types.map(id => Number(id)) : [Number(types)];
        
        // Payload should contain type field - backend expects 'type' array
        // Note: 'project' and 'tag' fields cause errors, so we use 'type' instead
        const payload = {
            type: typesArray
        };
        
        // Clean payload to remove any invalid fields that might cause backend errors
        const cleanPayload = cleanTaskUpdatePayload(payload);
        
        // Encrypt the payload (server expects encrypted data)
        let encryptedData = await encrypt(cleanPayload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        // Use the same endpoint as other updates
        const endpoint = UPDATE_TASK_MOBILE(serviceProvider, taskID, actualUserID);
        
        // Send encrypted data as raw string (backend expects encrypted data)
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        
        // Decrypt response
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task notifiers
 * @param {number} taskID - The task ID to update
 * @param {Array<number>} notifierIds - Array of notifier user IDs (use [0] to remove all)
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskNotifiers = async (taskID, notifierIds, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Convert notifierIds to array of numbers
        // If notifierIds is [0], it means remove all notifiers
        const notifiers = Array.isArray(notifierIds) 
            ? notifierIds.map(id => Number(id))
            : (notifierIds ? [Number(notifierIds)] : []);
        
        // Prepare payload - just the notifiers array
        const payload = {
            notifiers: notifiers
        };

        // Encrypt the payload (server expects encrypted data)
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_MOBILE(serviceProvider, taskID, actualUserID);
        
        // Send encrypted data as raw string (backend expects encrypted data)
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        
        // Decrypt response
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task end_date
 * @param {number} taskID - The task ID to update
 * @param {string} endDate - The end date in format "YYYY-MM-DD"
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskEndDate = async (taskID, endDate, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }
    // Allow empty string or "0" for deletion
    if (endDate === undefined || endDate === null) {
        throw new Error('End date is required (use empty string or "0" to delete)');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        // Use "0" for deletion, otherwise use the provided date
        const payload = {
            end_date: (endDate === '' || endDate === '0') ? '0' : String(endDate)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task start_time
 * @param {number} taskID - The task ID to update
 * @param {string} startTime - The start time in format "HH:mm:ss"
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskStartTime = async (taskID, startTime, userID = null, serviceProvider = 59) => {
    if (!taskID || !startTime) {
        throw new Error('Task ID and start time are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            start_time: String(startTime)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task end_time
 * @param {number} taskID - The task ID to update
 * @param {string} endTime - The end time in format "HH:mm:ss"
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskEndTime = async (taskID, endTime, userID = null, serviceProvider = 59) => {
    if (!taskID || !endTime) {
        throw new Error('Task ID and end time are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            end_time: String(endTime)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Update task schedule (start/end date and time) using unified updates object
 * @param {number} taskID - The task ID to update
 * @param {Object} updates - { startDate?: "YYYY-MM-DD" | "", endDate?: "YYYY-MM-DD" | "", startTime?: "HH:mm" | "", endTime?: "HH:mm" | "" }
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskSchedule = async (taskID, updates = {}, userID = null, serviceProvider = 59) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }
    if (!updates || typeof updates !== 'object') {
        throw new Error('Updates object is required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    const sanitizeTime = (t) => {
        if (t === '') return '';
        if (!t) return undefined;
        return String(t); // expect "HH:mm"
    };

    const payload = {};

    if (Object.prototype.hasOwnProperty.call(updates, 'startDate')) {
        payload.start_date = updates.startDate === '' ? '' : updates.startDate;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'endDate')) {
        payload.end_date = updates.endDate === '' ? '' : updates.endDate;
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'startTime')) {
        payload.start_time = sanitizeTime(updates.startTime);
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'endTime')) {
        payload.end_time = sanitizeTime(updates.endTime);
    }

    // Send a flat payload for update: { start_date, start_time, end_date, end_time }

    try {
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};
/**
 * Update task estimated effort
 * @param {number} taskID - The task ID to update
 * @param {number} estimated - The estimated effort in minutes
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @returns {Promise<Object>} The updated task data
 */
export const updateTaskEstimated = async (taskID, estimated, userID = null, serviceProvider = 59) => {
    if (!taskID || estimated === undefined || estimated === null) {
        throw new Error('Task ID and estimated effort are required');
    }

    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required for updating task');
    }

    try {
        const payload = {
            estimated: Number(estimated),
            duration: Number(estimated)
        };
        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const endpoint = UPDATE_TASK_ACCOMPLISHED(serviceProvider, taskID, actualUserID);
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
            },
            transformRequest: [(data) => data],
            timeout: 60000,
        });
        let decryptedResponse = response.data;
        if (typeof response.data === 'string') {
            try {
                const decrypted = fastDecrypt(response.data);
                decryptedResponse = decrypted?.data || decrypted;
            } catch (decryptError) {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                    throw new Error('Failed to parse response from server.');
                }
            }
        }
        return decryptedResponse?.data || decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Send email from task
 * @param {number} taskID - The task ID
 * @param {string} emailTitle - The email subject/title
 * @param {string} emailBody - The email body/content
 * @param {number} userID - The user ID (optional, will use tokenStorage if not provided)
 * @returns {Promise<Object>} The response data
 */
/**
 * Generate a message suggestion using Lee AI
 * @param {number} taskID - The task ID
 * @param {number} userID - The user ID
 * @param {number} tone - The tone of the message (default: 2)
 * @returns {Promise<string>} The generated message
 */
export const getLeeTaskMessage = async (taskID, userID, tone = 2) => {
    try {
        const actualUserID = userID || tokenStorage.getUserId();
        if (!actualUserID) {
            throw new Error('User ID is required');
        }

        const payload = { tone };

        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const res = await axiosApi.post(`/dailee/lee_task_messages/${taskID}`, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': String(actualUserID)
            },
            transformRequest: [(data) => data]
        });

        const data = fastDecrypt(res.data) || res.data;

        // Prefer "messages"; handle fallbacks if they appear
        if (data.messages) return data.messages;
        if (data.emailMessage) return data.emailMessage;
        if (data.chatMessage) return data.chatMessage;
        return '';
    } catch (error) {
        console.error('Error generating Lee message:', error);
        throw error;
    }
};

/**
 * Generate email subject and body using Lee
 * @param {number} taskID - The task ID
 * @param {number} userID - The user ID
 * @param {number} tone - The tone of the email (default: 2)
 * @returns {Promise<{subject: string, body: string}>} The generated email
 */
export const generateLeeEmail = async (taskID, userID, tone = 2) => {
    const actualUserID = userID || tokenStorage.getUserId();
    if (!actualUserID) {
        throw new Error('User ID is required');
    }

    const payload = { tone };

    // Encrypt the payload
    let encryptedData = await encrypt(payload);
    let payloadToSend = encryptedData.data;
    if (Array.isArray(encryptedData.data)) {
        if (encryptedData.data.length > 0) {
            payloadToSend = encryptedData.data[0];
        }
    }

    const res = await axiosApi.post(`/dailee/lee_task_email/${taskID}`, payloadToSend, {
        headers: {
            'Content-Type': 'text/plain',
            'userID': String(actualUserID)
        },
        transformRequest: [(data) => data]
    });

    const data = fastDecrypt(res.data) || res.data;
    return data;
};

/**
 * Send an email from a task
 * @param {number} taskID - The task ID
 * @param {number} senderUserId - The sender's user ID
 * @param {number} recipientUserId - The recipient's user ID
 * @param {string} title - The email title
 * @param {string} body - The email body
 * @returns {Promise<Object>} The response data
 */
export const sendEmailFromTask = async (taskID, senderUserId, recipientUserId, title, body) => {
    if (!taskID) {
        throw new Error('Task ID is required');
    }

    // Support legacy signature: (taskID, emailTitle, emailBody, userID)
    let actualSenderId = senderUserId;
    let actualRecipientId = recipientUserId;
    let actualTitle = title;
    let actualBody = body;

    // Legacy support check: (taskID, title, body, senderId)
    if (typeof senderUserId === 'string' && typeof recipientUserId === 'string') {
        actualTitle = senderUserId;
        actualBody = recipientUserId;
        actualSenderId = title || tokenStorage.getUserId();
        actualRecipientId = null;
    }

    if (!actualTitle || !actualTitle.trim()) {
        throw new Error('Email title is required');
    }
    if (!actualBody || !actualBody.trim()) {
        throw new Error('Email body is required');
    }

    try {
        const userId = actualSenderId || tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }

        const payload = {
            userID: actualRecipientId,
            emailTitle: actualTitle.trim(),
            emailBody: actualBody.trim()
        };

        // Encrypt the payload
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const res = await axiosApi.post(`/mobile/easeetask/email_from_task/${taskID}`, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'Userid': String(userId)
            },
            transformRequest: [(data) => data]
        });

        const data = fastDecrypt(res.data) || res.data;
        return data;
    } catch (error) {
        throw error;
    }
};

/**
 * Get tags for a task/user
 * @param {number} userID - The user ID
 * @param {number|number[]} organisationIDs - Single organisation ID or array of organisation IDs
 * @param {number} serviceProvider - The service provider ID (default: 59)
 * @param {boolean} userTags - Include user tags (default: true)
 * @param {boolean} defaultTags - Include default tags (default: true)
 * @returns {Promise<Object>} The tags data
 */
export const getTaskTags = async (userID, organisationIDs, serviceProvider = 59, userTags = true, defaultTags = true) => {
    if (!userID) {
        throw new Error('User ID is required');
    }
    if (!organisationIDs) {
        throw new Error('Organisation ID(s) is required');
    }

    try {
        const endpoint = GET_TASK_TAGS(serviceProvider, userID);
        // Build query parameters
        const params = {};
        
        // Handle organisationIDs - can be single ID or array
        if (Array.isArray(organisationIDs)) {
            params.organisationIDs = organisationIDs.join(',');
        } else {
            params.organisationIDs = organisationIDs.toString();
        }
        
        params.userTags = userTags ? '1' : '0';
        params.defaultTags = defaultTags ? '1' : '0';
        const response = await axiosApi.get(endpoint, {
            params: params,
            timeout: 30000,
        });
        // Decrypt response if needed - try decrypt endpoint first, then localDecrypt with local key
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
            // Try localDecrypt on decrypted data if it's still encrypted
            const decryptedData = decryptedResponse?.data || decryptedResponse;
            if (typeof decryptedData === 'string') {
                try {
                    const localDecrypted = localDecrypt(decryptedData);
                    if (localDecrypted) {
                        try {
                            const parsed = JSON.parse(localDecrypted);
                            return parsed;
                        } catch (parseError) {
                            return localDecrypted;
                        }
                    }
                } catch (localError) {
                }
            }
            return decryptedData;
        } catch (decryptError) {
            // If decrypt fails with 400, try local decryption with local key
            if (decryptError?.response?.status === 400 || decryptError?.response?.status === 500) {
                try {
                    const encryptedString = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                    const localDecrypted = localDecrypt(encryptedString);
                    if (localDecrypted) {
                        try {
                            const parsed = JSON.parse(localDecrypted);
                            return parsed;
                        } catch (parseError) {
                            return localDecrypted;
                        }
                    }
                } catch (localError) {
                }
            }
            // If response is already plain JSON, return it
            if (typeof response.data === 'object' && response.data !== null) {
                return response.data;
            }
            // If status is 200, try to return the response anyway
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new tag
 * @param {string} name - The tag name
 * @param {string} color - The tag color (hex format, e.g., "#FF5733")
 * @param {number} userID - The user ID (for personal tags)
 * @param {number} organisationID - The organisation ID (for organisation tags, only if admin)
 * @returns {Promise<Object>} The created tag data
 */
export const createTag = async (name, color, userID = null, organisationID = null, serviceProvider = 59) => {
    if (!name) {
        throw new Error('Tag name is required');
    }
    if (!userID && !organisationID) {
        throw new Error('Either userID or organisationID is required');
    }

    try {
        const payload = {
            typeName: name, // Backend expects typeName, not name
            service_provider: Number(serviceProvider),
        };
        
        // Only add color if provided
        if (color) {
            payload.color = color;
        }
        
        // Add userID for personal tags OR organisationID for org tags (not both)
        if (organisationID) {
            // Organisation tag – mark as non-custom (backend expects custom: 0)
            payload.organisationID = Number(organisationID);
            payload.custom = 0;
        } else if (userID) {
            // Personal tag – mark as custom/personal (backend expects custom: 1)
            payload.userID = Number(userID);
            payload.custom = 1;
        }

        // Encrypt the payload (backend expects encrypted data)
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const headerUserId = tokenStorage.getUserId && tokenStorage.getUserId();
        const headers = {
            'Content-Type': 'text/plain',
        };
        if (headerUserId) {
            headers['userID'] = String(headerUserId);
        }

        const response = await axiosApi.post(POST_ADD_TAG, payloadToSend, {
            headers,
            timeout: 30000,
        });

        // Decrypt response if needed
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            decryptedResponse = response.data;
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete tags
 * @param {number[]} tagIds - Array of tag IDs to delete
 * @param {number} userID - The user ID (for personal tags)
 * @param {number} organisationID - The organisation ID (for organisation tags)
 * @returns {Promise<Object>} The response data
 */
export const deleteTags = async (tagIds, userID = null, organisationID = null) => {
    if (!tagIds || !Array.isArray(tagIds) || tagIds.length === 0) {
        throw new Error('Tag IDs array is required');
    }
    if (!userID && !organisationID) {
        throw new Error('Either userID or organisationID is required');
    }

    try {
        const payload = {
            types: tagIds,
        };
        
        // Add userID or organisationID
        if (organisationID) {
            payload.organisationID = organisationID;
        }
        if (userID) {
            payload.userID = userID;
        }

        // Encrypt the payload (backend expects encrypted data)
        let encryptedData = await encrypt(payload);
        let payloadToSend = encryptedData.data;

        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        const headerUserId = tokenStorage.getUserId && tokenStorage.getUserId();
        const headers = {
            'Content-Type': 'text/plain',
        };
        if (headerUserId) {
            headers['userID'] = String(headerUserId);
        }

        const response = await axiosApi.delete(DELETE_TAGS, {
            data: payloadToSend,
            headers,
            timeout: 30000,
        });

        // Decrypt response if needed
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            decryptedResponse = response.data;
        }

        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Get received tasks for a user
 * @param {number} userID - The user ID
 * @param {number|number[]} organisationIDs - Single organisation ID or array of organisation IDs
 * @param {string} pocket - Pocket filter (optional)
 * @param {string} orderBy - Field to sort by (optional)
 * @param {string} order - Sort order (asc/desc) (optional)
 * @param {number} limit - Maximum number of tasks to return (optional)
 * @param {number} offset - Number of tasks to skip (optional)
 * @returns {Promise<Object>} The tasks data
 */
export const getReceivedTasks = async (userID, organisationIDs, pocket = null, orderBy = null, order = null, limit = 200, offset = 0) => {
    if (!userID) {
        throw new Error('User ID is required');
    }
    if (!organisationIDs) {
        throw new Error('Organisation ID(s) is required');
    }

    try {
        const endpoint = GET_RECEIVED_TASKS(userID);
        // Build query parameters
        const params = {};
        
        // Handle organisationIDs - can be single ID or array
        if (Array.isArray(organisationIDs)) {
            params.organisationIDs = organisationIDs.join(',');
        } else {
            params.organisationIDs = organisationIDs.toString();
        }
        
        if (pocket) {
            params.pocket = pocket;
        }
        if (orderBy) {
            params.orderBy = orderBy;
        }
        if (order) {
            params.order = order;
        }
        if (limit) {
            params.limit = limit;
        }
        if (offset) {
            params.offset = offset;
        }
        const response = await axiosApi.get(endpoint, {
            params: params,
            timeout: 60000,
        });
        // Decrypt response if needed (similar to getTasks)
        let decryptedResponse;
        try {
            if (typeof response.data === 'string') {
                decryptedResponse = fastDecrypt(response.data);
            } else if (response.data && typeof response.data === 'object') {
                decryptedResponse = response.data;
            } else {
                decryptedResponse = response.data;
            }
            return decryptedResponse?.data || decryptedResponse;
        } catch (decryptError) {
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get sent tasks for a user
 * @param {number} userID - The user ID
 * @param {number|number[]} organisationIDs - Single organisation ID or array of organisation IDs
 * @param {string} pocket - Pocket filter (optional)
 * @param {string} orderBy - Field to sort by (optional)
 * @param {string} order - Sort order (asc/desc) (optional)
 * @param {number} limit - Maximum number of tasks to return (optional)
 * @param {number} offset - Number of tasks to skip (optional)
 * @returns {Promise<Object>} The tasks data
 */
export const getSentTasks = async (userID, organisationIDs, pocket = null, orderBy = null, order = null, limit = 200, offset = 0) => {
    if (!userID) {
        throw new Error('User ID is required');
    }
    if (!organisationIDs) {
        throw new Error('Organisation ID(s) is required');
    }

    try {
        const endpoint = GET_SENT_TASKS(userID);
        // Build query parameters
        const params = {};
        
        // Handle organisationIDs - can be single ID or array
        if (Array.isArray(organisationIDs)) {
            params.organisationIDs = organisationIDs.join(',');
        } else {
            params.organisationIDs = organisationIDs.toString();
        }
        
        if (pocket) {
            params.pocket = pocket;
        }
        if (orderBy) {
            params.orderBy = orderBy;
        }
        if (order) {
            params.order = order;
        }
        if (limit) {
            params.limit = limit;
        }
        if (offset) {
            params.offset = offset;
        }
        const response = await axiosApi.get(endpoint, {
            params: params,
            timeout: 60000,
        });
        // Decrypt response if needed (similar to getTasks)
        let decryptedResponse;
        try {
            if (typeof response.data === 'string') {
                decryptedResponse = fastDecrypt(response.data);
            } else if (response.data && typeof response.data === 'object') {
                decryptedResponse = response.data;
            } else {
                decryptedResponse = response.data;
            }
            return decryptedResponse?.data || decryptedResponse;
        } catch (decryptError) {
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get assignee list for an organisation
 * @param {number} organisationID - The organisation ID
 * @param {Object} options - Optional parameters
 * @param {number} options.memberListID - The member list ID (optional)
 * @param {string} options.order - Order parameter: 'notifiers' or 'assignee' (optional)
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {number} options.limit - Limit for pagination (optional)
 * @returns {Promise<Object>} The assignee list data
 */
export const getAssigneeList = async (organisationID, options = {}) => {
    if (!organisationID) {
        throw new Error('Organisation ID is required');
    }

    // Handle legacy call signature (memberListID as second parameter)
    let memberListID = null;
    let order = null;
    let offset = 0;
    let limit = null;
    let search = null;
    
    if (typeof options === 'number' || options === null) {
        // Legacy signature: getAssigneeList(orgId, memberListID)
        memberListID = options;
    } else if (typeof options === 'object') {
        // New signature: getAssigneeList(orgId, { memberListID, order, offset, limit, search })
        memberListID = options.memberListID;
        order = options.order;
        offset = options.offset !== undefined ? options.offset : 0;
        limit = options.limit;
        search = options.search;
    }

    try {
        const endpoint = GET_ASSIGNEE_LIST(organisationID);
        // Build query parameters
        const params = {};
        if (memberListID !== null && memberListID !== undefined) {
            params.memberListID = memberListID;
        }
        if (order) {
            params.order = order;
        }
        if (offset !== undefined && offset !== null) {
            params.offset = offset;
        }
        if (limit !== undefined && limit !== null && limit !== '') {
            params.limit = limit;
        }
        if (search !== null && search !== undefined && search !== '') {
            params.search = search;
        }
        
        console.log('🌐 [getAssigneeList] API Call:', {
            endpoint,
            organisationID,
            params,
            fullUrl: `${endpoint}?${new URLSearchParams(params).toString()}`
        });
        
        const response = await axiosApi.get(endpoint, {
            params: params,
            timeout: 30000,
        });
        // Decrypt response if needed
        let decryptedResponse;
        try {
            if (typeof response.data === 'string') {
                decryptedResponse = fastDecrypt(response.data);
            } else if (response.data && typeof response.data === 'object') {
                decryptedResponse = response.data;
            } else {
                decryptedResponse = response.data;
            }
            return decryptedResponse?.data || decryptedResponse;
        } catch (decryptError) {
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get owners for an organisation
 * @param {number} organisationID - The organisation ID
 * @param {Object} options - Optional parameters
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {number} options.limit - Limit for pagination (default: 50)
 * @param {string} options.search - Search by name, firstname, lastname or email
 * @returns {Promise<Object>} The owners list data in format { _meta: { status }, owners: [...] }
 */
export const getOwners = async (organisationID, options = {}) => {
    if (!organisationID) {
        throw new Error('Organisation ID is required');
    }

    const offset = options.offset !== undefined ? options.offset : 0;
    const limit = options.limit !== undefined ? options.limit : 50;
    const search = options.search || null;

    try {
        const endpoint = GET_OWNERS(organisationID);
        // Build query parameters
        const params = {};
        if (offset !== undefined && offset !== null) {
            params.offset = offset;
        }
        if (limit !== undefined && limit !== null) {
            params.limit = limit;
        }
        if (search !== null && search !== undefined && search !== '') {
            params.search = search;
        }
        
        console.log('🌐 [getOwners] API Call:', {
            endpoint,
            organisationID,
            params,
            fullUrl: `${endpoint}?${new URLSearchParams(params).toString()}`
        });
        
        // Get user ID from session for header
        const userId = tokenStorage.getUserId();
        
        const headers = {};
        if (userId) {
            headers['userID'] = userId.toString(); // Add userID header from session (using userID instead of user for CORS compatibility)
        }
        
        const response = await axiosApi.get(endpoint, {
            params: params,
            timeout: 30000,
            headers: headers
        });
        
        // Decrypt response if needed
        let decryptedResponse;
        try {
            if (typeof response.data === 'string') {
                decryptedResponse = fastDecrypt(response.data);
            } else if (response.data && typeof response.data === 'object') {
                decryptedResponse = response.data;
            } else {
                decryptedResponse = response.data;
            }

            // Normalizăm răspunsul la format unificat:
            // {
            //   _meta: { status: 'SUCCESS' },
            //   owners: [...],
            //   responsibleList: { count, total, offset, records: [...] }
            // }

            // Caz 1: backend trimite deja { _meta, responsibleList: { records: [...] } }
            let ownersArray;
            let meta = decryptedResponse?._meta;

            if (Array.isArray(decryptedResponse?.responsibleList?.records)) {
                ownersArray = decryptedResponse.responsibleList.records;
            } else if (Array.isArray(decryptedResponse)) {
                // Caz 2: backend trimite direct array de owners
                ownersArray = decryptedResponse;
                if (!meta) {
                    meta = { status: 'SUCCESS' };
                }
            } else if (decryptedResponse?.owners && Array.isArray(decryptedResponse.owners)) {
                ownersArray = decryptedResponse.owners;
            } else if (decryptedResponse?.data && Array.isArray(decryptedResponse.data)) {
                ownersArray = decryptedResponse.data;
            } else if (Array.isArray(decryptedResponse?.data?.records)) {
                ownersArray = decryptedResponse.data.records;
            } else {
                ownersArray = [];
            }

            const safeOffset = typeof offset === 'number' ? offset : 0;
            const count = ownersArray.length;
            const total =
                decryptedResponse?.responsibleList?.total ??
                decryptedResponse?.responsibleList?.count ??
                decryptedResponse?.total ??
                decryptedResponse?.count ??
                count;

            return {
                _meta: meta || { status: 'SUCCESS' },
                owners: ownersArray,
                responsibleList: {
                    count,
                    total,
                    offset: safeOffset,
                    records: ownersArray
                }
            };
        } catch (decryptError) {
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Get notifiers for an organisation
 * @param {number} organisationID - The organisation ID
 * @param {Object} options - Optional parameters
 * @param {number} options.offset - Offset for pagination (default: 0)
 * @param {number} options.limit - Limit for pagination (default: 50)
 * @param {string} options.search - Search by name, firstname, lastname or email
 * @returns {Promise<Object>} The notifiers list data in unified format:
 * {
 *   _meta: { status: 'SUCCESS' },
 *   responsibleList: { count, total, offset, records: [...] }
 * }
 */
export const getNotifiers = async (organisationID, options = {}) => {
    if (!organisationID) {
        throw new Error('Organisation ID is required');
    }

    const offset = options.offset !== undefined ? options.offset : 0;
    const limit = options.limit !== undefined ? options.limit : 50;
    const search = options.search || null;

    try {
        const endpoint = GET_NOTIFIERS(organisationID);
        const params = {};
        if (offset !== undefined && offset !== null) {
            params.offset = offset;
        }
        if (limit !== undefined && limit !== null) {
            params.limit = limit;
        }
        if (search !== null && search !== undefined && search !== '') {
            params.search = search;
        }

        console.log('🌐 [getNotifiers] API Call:', {
            endpoint,
            organisationID,
            params,
            fullUrl: `${endpoint}?${new URLSearchParams(params).toString()}`
        });

        const userId = tokenStorage.getUserId();
        const headers = {};
        if (userId) {
            headers['userID'] = userId.toString();
        }

        const response = await axiosApi.get(endpoint, {
            params,
            timeout: 30000,
            headers
        });

        let decryptedResponse;
        try {
            if (typeof response.data === 'string') {
                decryptedResponse = fastDecrypt(response.data);
            } else if (response.data && typeof response.data === 'object') {
                decryptedResponse = response.data;
            } else {
                decryptedResponse = response.data;
            }

            // Backend poate trimite fie array simplu, fie deja ambalat
            let records;
            let meta = decryptedResponse?._meta;

            if (Array.isArray(decryptedResponse?.responsibleList?.records)) {
                // Caz nou: { _meta, responsibleList: { records: [...] } }
                records = decryptedResponse.responsibleList.records;
            } else if (Array.isArray(decryptedResponse)) {
                records = decryptedResponse;
                if (!meta) {
                    meta = { status: 'SUCCESS' };
                }
            } else if (Array.isArray(decryptedResponse?.records)) {
                records = decryptedResponse.records;
            } else if (Array.isArray(decryptedResponse?.data?.records)) {
                records = decryptedResponse.data.records;
            } else if (Array.isArray(decryptedResponse?.data)) {
                records = decryptedResponse.data;
            } else {
                records = [];
            }

            const safeOffset = typeof offset === 'number' ? offset : 0;
            const count = records.length;
            const total =
                decryptedResponse?.responsibleList?.total ??
                decryptedResponse?.responsibleList?.count ??
                decryptedResponse?.total ??
                decryptedResponse?.count ??
                count;

            return {
                _meta: meta || { status: 'SUCCESS' },
                responsibleList: {
                    count,
                    total,
                    offset: safeOffset,
                    records
                }
            };
        } catch (decryptError) {
            if (response?.status === 200) {
                return response.data;
            }
            throw decryptError;
        }
    } catch (error) {
        throw error;
    }
};

/**
 * Start activity
 * @param {Object} activityData - The activity data
 * @param {string} activityData.start_activity - Start date/time in format "YYYY-MM-DD HH:mm:ss"
 * @param {string} activityData.work_location - Work location: "home", "office", or "onsite"
 * @param {string} activityData.reserved_start_hour - Reserved start hour in format "HH:mm:ss"
 * @param {string} activityData.reserved_end_hour - Reserved end hour in format "HH:mm:ss"
 * @param {number} activityData.organisationID - Organisation ID
 * @param {number} activityData.work_organisation_id - Work organisation ID
 * @param {boolean} activityData.automatically_end - Whether to automatically end
 * @param {string} activityData.automatically_end_at - Auto end date/time in format "YYYY-MM-DD HH:mm:ss" (if automatically_end is true)
 * @returns {Promise<Object>} The response data
 */
export const startActivity = async (activityData) => {
    try {
        const endpoint = POST_START_ACTIVITY;
        // Encrypt the data - same format as postTask
        let encryptedData = await encrypt(activityData);
        // Extract the encrypted string from the response
        let payloadToSend = encryptedData.data;
        
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Send as raw string with Content-Type: text/plain and userID in header
        const response = await axiosApi.post(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': userId.toString()
            },
            transformRequest: [(data) => {
                // Return data as-is, don't let axios convert it to JSON
                return data;
            }],
            timeout: 30000,
        });
        // Decrypt response
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Activity started successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }
        return decryptedResponse;
    } catch (error) {
        if (error.response) {
        }
        throw error;
    }
};

/**
 * Update summary (End activity)
 * @param {Object} activityData - The activity data
 * @param {string} activityData.end_activity - End date/time in format "YYYY-MM-DD HH:mm:ss"
 * @returns {Promise<Object>} The response data
 */
export const updateSummary = async (activityData) => {
    try {
        const endpoint = PUT_UPDATE_SUMMARY;
        // Encrypt the data - same format as startActivity
        let encryptedData = await encrypt(activityData);
        // Extract the encrypted string from the response
        let payloadToSend = encryptedData.data;
        
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Send as PUT request with raw string, Content-Type: text/plain and userID in header only
        // organisation_id should be in payload, not in header
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': userId.toString()
            },
            transformRequest: [(data) => {
                // Return data as-is, don't let axios convert it to JSON
                return data;
            }],
            timeout: 30000,
        });
        // Decrypt response
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Activity updated successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }
        return decryptedResponse;
    } catch (error) {
        if (error.response) {
        }
        throw error;
    }
};

/**
 * Create Pomodoro timer for a task
 * @param {number} projectID - The project/task ID
 * @param {Object} pomodoroData - The pomodoro data
 * @param {string} pomodoroData.start_timer - Start date/time in format "YYYY-MM-DD HH:mm:ss"
 * @returns {Promise<Object>} The response data
 */
export const createPomodoroTimer = async (projectID, pomodoroData) => {
    try {
        const endpoint = POST_CREATE_POMODORO_TIMER(projectID);
        // Encrypt the data - same format as startActivity
        let encryptedData = await encrypt(pomodoroData);
        // Extract the encrypted string from the response
        let payloadToSend = encryptedData.data;
        
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Send as raw string with Content-Type: text/plain and userID in header
        const response = await axiosApi.post(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': userId.toString()
            },
            transformRequest: [(data) => {
                // Return data as-is, don't let axios convert it to JSON
                return data;
            }],
            timeout: 30000,
        });
        // Decrypt response
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Pomodoro timer created successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }
        return decryptedResponse;
    } catch (error) {
        if (error.response) {
        }
        throw error;
    }
};

/**
 * End Pomodoro timer for a task
 * @param {number} projectID - The project/task ID
 * @param {Object} pomodoroData - The pomodoro end data
 * @param {string} pomodoroData.end_timer - End date/time in format "YYYY-MM-DD HH:mm:ss"
 * @param {number} pomodoroData.time_spent - Time spent in minutes
 * @returns {Promise<Object>} The response data
 */
export const endPomodoroTimer = async (projectID, pomodoroData) => {
    try {
        const endpoint = PUT_END_POMODORO_TIMER(projectID);
        // Encrypt the data - same format as startActivity
        let encryptedData = await encrypt(pomodoroData);
        // Extract the encrypted string from the response
        let payloadToSend = encryptedData.data;
        
        if (Array.isArray(encryptedData.data)) {
            if (encryptedData.data.length > 0) {
                payloadToSend = encryptedData.data[0];
            }
        }

        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Send as PUT request with raw string, Content-Type: text/plain and userID in header
        const response = await axiosApi.put(endpoint, payloadToSend, {
            headers: {
                'Content-Type': 'text/plain',
                'userID': userId.toString()
            },
            transformRequest: [(data) => {
                // Return data as-is, don't let axios convert it to JSON
                return data;
            }],
            timeout: 30000,
        });
        // Decrypt response
        let decryptedResponse;
        try {
            decryptedResponse = fastDecrypt(response.data);
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Pomodoro timer ended successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }
        return decryptedResponse;
    } catch (error) {
        if (error.response) {
        }
        throw error;
    }
};

/**
 * Get summary for an organisation
 * @param {number} organisationID - The organisation ID
 * @returns {Promise<Object>} The summary data
 */
export const getSummary = async (organisationID) => {
    try {
        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Construct URL with organisationID query parameter
        const endpoint = `${GET_SUMMARY}?organisationID=${organisationID}`;
        // Send GET request with userID in header
        const response = await axiosApi.get(endpoint, {
            headers: {
                'userID': userId.toString()
            },
            timeout: 30000,
        });
        // Decrypt response with local key (similar to getTaskTags)
        let decryptedResponse;
        try {
            // First try to decrypt with server key
            if (typeof response.data === 'string') {
                try {
                decryptedResponse = fastDecrypt(response.data);
                } catch (serverDecryptError) {
                    // If server key fails, try local key
                    const localKey = tokenStorage.getEncryptionKey();
                    const localIV = tokenStorage.getEncryptionIV();
                    
                    if (localKey && localIV) {
                        decryptedResponse = await localDecrypt(response.data, localKey, localIV);
            } else {
                try {
                    decryptedResponse = JSON.parse(response.data);
                } catch (parseError) {
                            throw new Error('Local encryption key/IV not available and JSON parse failed');
                        }
                    }
                }
            } else {
                decryptedResponse = response.data;
            }
        } catch (decryptError) {
            decryptedResponse = response.data;
        }
        return decryptedResponse;
    } catch (error) {
        if (error.response) {
        }
        throw error;
    }
};

/**
 * Get summary history
 * @param {number} organisationID - The organisation ID
 * @param {string} dateFilter - Date filter: 'day', 'week', 'month', etc. (default: 'day')
 * @returns {Promise<Object>} The summary history data
 */
export const getSummaryHistory = async (organisationID, dateFilter = 'day') => {
    try {
        // Get user ID from token storage
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }
        // Construct URL with organisationID and dateFilter query parameters
        const endpoint = `${GET_SUMMARY_HISTORY}?organisationID=${organisationID}&dateFilter=${dateFilter}`;
        // Send GET request with userID in header
        const response = await axiosApi.get(endpoint, {
            headers: {
                'userID': userId.toString()
            },
            timeout: 30000,
        });
        // Decrypt response with local key (similar to getSummary)
        let decryptedResponse;
        try {
            // First try to decrypt with server key
            if (typeof response.data === 'string') {
                try {
                    decryptedResponse = fastDecrypt(response.data);
                } catch (serverDecryptError) {
                    // If server key fails, try local key
                    const localKey = tokenStorage.getEncryptionKey();
                    const localIV = tokenStorage.getEncryptionIV();
                    
                    if (localKey && localIV) {
                        decryptedResponse = await localDecrypt(response.data, localKey, localIV);
                    } else {
                        try {
                            decryptedResponse = JSON.parse(response.data);
                        } catch (parseError) {
                            throw new Error('Local encryption key/IV not available and JSON parse failed');
                        }
                    }
                }
            } else {
                decryptedResponse = response.data;
            }
        } catch (decryptError) {
            // If status is 200, return success even if decryption fails
            if (response?.status === 200) {
                return {
                    success: true,
                    message: 'Summary history retrieved successfully, but response could not be decrypted.',
                    status: 200,
                    encryptedResponse: response.data
                };
            }
            throw decryptError;
        }
        return decryptedResponse;
    } catch (error) {
        throw error;
    }
};

/**
 * Approve day off request
 * @param {number} dayoffID - The day off request ID
 * @param {string} adminComment - Admin comment (optional)
 * @returns {Promise<Object>} The response data
 */
export const approveDayOff = async (dayoffID, adminComment = null) => {
    if (!dayoffID) {
        throw new Error('Day off ID is required');
    }

    try {
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }

        const endpoint = POST_APPROVE_DAY_OFF(dayoffID);
        const response = await axiosApi.post(endpoint, {
            admin_comment: adminComment
        }, {
            headers: {
                'userID': userId.toString()
            }
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Decline day off request
 * @param {number} dayoffID - The day off request ID
 * @param {string} adminComment - Admin comment (optional)
 * @returns {Promise<Object>} The response data
 */
export const declineDayOff = async (dayoffID, adminComment = null) => {
    if (!dayoffID) {
        throw new Error('Day off ID is required');
    }

    try {
        const userId = tokenStorage.getUserId();
        if (!userId) {
            throw new Error('User ID is required');
        }

        const endpoint = POST_DECLINE_DAY_OFF(dayoffID);
        const response = await axiosApi.post(endpoint, {
            admin_comment: adminComment
        }, {
            headers: {
                'userID': userId.toString()
            }
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Generate day-off prompt (document text)
 * @param {Object} params
 * @returns {Promise<Object>} { document_title, document_text, date }
 */
export const generateDayOffPrompt = async ({ organisationID, userID, startDate, endDate, typeId, includeNationalDays = false, includeSaturdays = false, includeSundays = false }) => {
    const orgId = Number(
        typeof organisationID === 'object'
            ? (organisationID.id || organisationID.organisation_id || organisationID.ID)
            : organisationID
    );

    if (!orgId || Number.isNaN(orgId)) {
        console.error('[DayOff] organisationID invalid în generateDayOffPrompt:', organisationID);
        throw new Error('Invalid organisationID for day-off prompt');
    }

    // Call API to generate prompt
    const response = await axiosApi.post(
        `/mobile/dailee/user-daysOff/generate_prompt/${orgId}`,
        {
            start_date: startDate,
            end_date: endDate,
            type_id: typeId || null,
            include_national_day: includeNationalDays,
            include_saturday: includeSaturdays,
            include_sunday: includeSundays
        },
        {
            headers: {
                'userID': String(userID)
            }
        }
    );

    // Decrypt response if needed (similar to fetchDayOffTypes)
    const raw = response?.data;
    const decrypted = fastDecrypt(raw) || raw;

    // Structure: { _meta: {...}, data: { document_title, document_text, date } }
    // OR just { document_title, document_text, date }
    const data = decrypted?.data || decrypted;
    const { document_title, document_text, date } = data || {};
    const payload = { document_title, document_text, date };
    console.log('[DayOff] Prompt payload used (plain JSON via axiosApi):', payload);
    return payload; // { document_title, document_text, date }
};

/**
 * Create day-off request
 * @param {Object} params
 * @returns {Promise<Object>} The response data
 */
export const createDayOffRequest = async ({ organisationID, userID, startDate, endDate, title, description, typeId, includeNationalDays = false, includeSaturdays = false, includeSundays = false }) => {
    const orgId = Number(
        typeof organisationID === 'object'
            ? (organisationID.id || organisationID.organisation_id || organisationID.ID)
            : organisationID
    );

    if (!orgId || Number.isNaN(orgId)) {
        console.error('[DayOff] organisationID invalid în createDayOffRequest:', organisationID);
        throw new Error('Invalid organisationID for create day-off');
    }

    const response = await axiosApi.post(
        `/mobile/dailee/user-daysOff/${orgId}`,
        {
            start_date: startDate,
            end_date: endDate,
            title_day_off: title,
            description: description,
            type_id: typeId || null,
            include_national_day: includeNationalDays,
            include_saturday: includeSaturdays,
            include_sunday: includeSundays
        },
        {
            headers: {
                'userID': String(userID)
            }
        }
    );

    return response?.data;
};

/**
 * Fetch day-off types
 * @param {number} organisationID
 * @param {number} userID
 * @returns {Promise<Array>} List of day-off types
 */
export const fetchDayOffTypes = async (organisationID, userID) => {
    const orgId = Number(
        typeof organisationID === 'object'
            ? (organisationID.id || organisationID.organisation_id || organisationID.ID)
            : organisationID
    );

    if (!orgId || Number.isNaN(orgId)) {
        console.error('[DayOff] organisationID invalid în fetchDayOffTypes:', organisationID);
        throw new Error('Invalid organisationID for day-off types');
    }

    console.log('[DayOff] Fetching day-off types...', {
        url: `/easeetask/get_day_off_type/${orgId}`,
        organisationID: orgId,
        rawOrganisationID: organisationID,
        userID
    });

    const res = await axiosApi.get(
        `/easeetask/get_day_off_type/${orgId}`,
        { headers: { userID: String(userID) } }
    );

    console.log('[DayOff] Raw response for day-off types:', res);

    // Răspunsul poate veni criptat
    const raw = res?.data;
    const decrypted = fastDecrypt(raw) || raw;
    console.log('[DayOff] Decrypted data for day-off types:', decrypted);

    // Dacă decrypted este deja un array, îl folosim direct
    if (Array.isArray(decrypted)) {
        console.log('[DayOff] Decrypted is directly an array:', decrypted);
        return decrypted;
    }

    const json = decrypted?.data || decrypted || {};
    console.log('[DayOff] Parsed JSON for day-off types:', json);

    let records =
        json.data?.records ||
        json.records ||
        json.data ||
        json.list ||
        json.types ||
        [];

    // Dacă încă nu e array, dar json are câteva chei generice, încearcă să-l transforme
    if (!Array.isArray(records) && Array.isArray(json)) {
        records = json;
    }

    console.log('[DayOff] Extracted records for day-off types (final):', records, 'isArray=', Array.isArray(records));

    return Array.isArray(records) ? records : [];
};

/**
 * Get permission users (assignees with permissions)
 * @param {number} organisationID
 * @param {number} userID
 * @returns {Promise<Array>} List of users with permissions
 */
export const getPermissionUsers = async (organisationID, userID) => {
    const orgId = Number(
        typeof organisationID === 'object'
            ? (organisationID.id || organisationID.organisation_id || organisationID.ID)
            : organisationID
    );

    if (!orgId || Number.isNaN(orgId)) {
        console.error('[TimeOff] organisationID invalid în getPermissionUsers:', organisationID);
        throw new Error('Invalid organisationID for permission users');
    }

    const response = await axiosApi.get(
        `/easeetask/assignee/${orgId}?order=permissions`,
        { headers: { userID: String(userID) } }
    );

    const raw = response?.data;
    const decrypted = fastDecrypt(raw) || raw;
    const data = decrypted?.data || decrypted || {};
    
    // Filtrăm doar utilizatorii care au permisiuni
    const records = data.responsibleList?.records || data.records || [];
    const usersWithPermissions = Array.isArray(records) ? records.filter(u => u.permissionData) : [];
    
    return usersWithPermissions;
};
